import { ErrorBoundary } from "@datadog/browser-rum-react";
import { CustomSnackbarProvider } from "@redotech/redo-web/snackbar";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  REDO_PARTNERS_APP_VERSION,
} from "./config";
import { initObservability } from "./datadog-rum";
import { ErrorFallback } from "./error-fallback";
import { router } from "./router";
import { shouldInitObservability } from "./should-init-observability";
import { stripTokenFromUrl } from "./strip-token";
import "./styles.css";

// Strip ?token= before any other code (including RUM session replay)
// can observe it. The magic-link token is short-lived but valid for
// ~1 hour from email send; defense in depth complements the RUM
// `beforeSend` hook in `./datadog-rum.ts`.
stripTokenFromUrl();

if (
  shouldInitObservability({
    nodeEnv: process.env.NODE_ENV,
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
  })
) {
  // Defensive: a bad init (DD CDN unreachable, malformed args) shouldn't
  // take the whole app down. console.error so the failure is visible
  // in dev and forwarded to logs in prod.
  try {
    initObservability({
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      version: REDO_PARTNERS_APP_VERSION,
      env: process.env.NODE_ENV || "production",
    });
  } catch (err) {
    console.error("Datadog observability init failed", err);
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Fail fast on client-side errors (4xx) but allow a couple
      // retries on transient network/5xx blips. AuthDataContext sets
      // its own retry policy for the `me` query.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById("root");
if (!container) {
  throw new Error("Missing #root element");
}

createRoot(container).render(
  <ErrorBoundary fallback={ErrorFallback}>
    <QueryClientProvider client={queryClient}>
      <CustomSnackbarProvider>
        <RouterProvider router={router} />
      </CustomSnackbarProvider>
    </QueryClientProvider>
  </ErrorBoundary>,
);
